import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Image, Search, Share2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'Romain Trallero',
    image_url: '',
    category: 'Organisation',
    published: false,
    // SEO fields
    seo_title: '',
    seo_description: '',
    seo_keywords: [],
    // Image fields
    image_social: '',
    image_square: '',
    image_thumbnail: '',
  });
  const [keywordsInput, setKeywordsInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);

  useEffect(() => {
    if (isEditing) {
      const fetchPost = async () => {
        try {
          const token = localStorage.getItem('admin_token');
          const response = await axios.get(`${API}/admin/blog/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setFormData(response.data);
          if (response.data.seo_keywords?.length) {
            setKeywordsInput(response.data.seo_keywords.join(', '));
          }
        } catch (error) {
          toast.error('Erreur lors du chargement de l\'article');
          navigate('/admin/blog');
        } finally {
          setFetching(false);
        }
      };
      fetchPost();
    }
  }, [id, isEditing, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Auto-generate slug from title
      ...(name === 'title' && !isEditing ? { 
        slug: value.toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, ''),
        // Auto-fill SEO title if empty
        ...(prev.seo_title === '' ? { seo_title: value } : {})
      } : {})
    }));
  };

  const handleKeywordsChange = (value) => {
    setKeywordsInput(value);
    const keywords = value.split(',').map(k => k.trim()).filter(k => k);
    setFormData(prev => ({ ...prev, seo_keywords: keywords }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug || !formData.excerpt || !formData.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      if (isEditing) {
        await axios.put(`${API}/admin/blog/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/admin/blog`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article créé');
      }
      
      navigate('/admin/blog');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/blog')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-rm-text">
            {isEditing ? 'Modifier l\'article' : 'Nouvel article'}
          </h1>
          <p className="text-rm-text-secondary">
            {isEditing ? 'Mets à jour ton article' : 'Crée un nouveau contenu pour le blog'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contenu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Comment organiser son entreprise..."
                    className="mt-1"
                    data-testid="post-title"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug URL *</Label>
                  <Input
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="comment-organiser-entreprise"
                    className="mt-1"
                    data-testid="post-slug"
                  />
                </div>
                <div>
                  <Label htmlFor="excerpt">Extrait *</Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    placeholder="Un court résumé de l'article..."
                    rows={3}
                    className="mt-1"
                    data-testid="post-excerpt"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Contenu (HTML) *</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="<h2>Introduction</h2><p>Ton contenu ici...</p>"
                    rows={15}
                    className="mt-1 font-mono text-sm"
                    data-testid="post-content"
                  />
                  <p className="text-xs text-rm-text-secondary mt-1">
                    Utilisez des balises HTML pour formater le contenu (h2, h3, p, ul, li, strong, etc.)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* SEO Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-600" />
                  SEO - Référencement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="seo_title">Méta-titre (60 caractères max)</Label>
                  <Input
                    id="seo_title"
                    name="seo_title"
                    value={formData.seo_title}
                    onChange={handleChange}
                    placeholder="Titre optimisé pour les moteurs de recherche..."
                    className="mt-1"
                    maxLength={60}
                    data-testid="post-seo-title"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.seo_title?.length || 0}/60 caractères
                  </p>
                </div>
                <div>
                  <Label htmlFor="seo_description">Méta-description (160 caractères max)</Label>
                  <Textarea
                    id="seo_description"
                    name="seo_description"
                    value={formData.seo_description}
                    onChange={handleChange}
                    placeholder="Description qui apparaîtra dans les résultats de recherche Google..."
                    rows={3}
                    className="mt-1"
                    maxLength={160}
                    data-testid="post-seo-description"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.seo_description?.length || 0}/160 caractères
                  </p>
                </div>
                <div>
                  <Label htmlFor="seo_keywords">Mots-clés (séparés par des virgules)</Label>
                  <Input
                    id="seo_keywords"
                    value={keywordsInput}
                    onChange={(e) => handleKeywordsChange(e.target.value)}
                    placeholder="assistant virtuel, productivité, délégation..."
                    className="mt-1"
                    data-testid="post-seo-keywords"
                  />
                  {formData.seo_keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.seo_keywords.map((kw, i) => (
                        <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Images Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-purple-600" />
                  Images & Partage Social
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="image_url">Image principale (article)</Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="mt-1"
                    data-testid="post-image-url"
                  />
                  {formData.image_url && (
                    <img 
                      src={formData.image_url}
                      alt="Preview"
                      className="mt-2 rounded-lg w-full h-40 object-cover"
                    />
                  )}
                </div>
                <div>
                  <Label htmlFor="image_social">Image réseaux sociaux (1200x630)</Label>
                  <Input
                    id="image_social"
                    name="image_social"
                    value={formData.image_social}
                    onChange={handleChange}
                    placeholder="https://... (image optimisée pour Facebook, LinkedIn, Twitter)"
                    className="mt-1"
                    data-testid="post-image-social"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Format recommandé : 1200x630 pixels pour un affichage optimal sur les réseaux sociaux
                  </p>
                  {formData.image_social && (
                    <img 
                      src={formData.image_social}
                      alt="Social Preview"
                      className="mt-2 rounded-lg w-full h-32 object-cover border border-purple-200"
                    />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="image_square">Image carrée (1:1)</Label>
                    <Input
                      id="image_square"
                      name="image_square"
                      value={formData.image_square}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="mt-1"
                      data-testid="post-image-square"
                    />
                    {formData.image_square && (
                      <img 
                        src={formData.image_square}
                        alt="Square Preview"
                        className="mt-2 rounded-lg w-24 h-24 object-cover"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="image_thumbnail">Miniature</Label>
                    <Input
                      id="image_thumbnail"
                      name="image_thumbnail"
                      value={formData.image_thumbnail}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="mt-1"
                      data-testid="post-image-thumbnail"
                    />
                    {formData.image_thumbnail && (
                      <img 
                        src={formData.image_thumbnail}
                        alt="Thumbnail Preview"
                        className="mt-2 rounded-lg w-20 h-14 object-cover"
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Publication</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="published">Publier</Label>
                  <Switch
                    id="published"
                    checked={formData.published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
                    data-testid="post-published"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-rm-primary hover:bg-rm-primary/90 text-white"
                  data-testid="post-save"
                >
                  {loading ? 'Enregistrement...' : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEditing ? 'Mettre à jour' : 'Créer l\'article'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Métadonnées</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="author">Auteur</Label>
                  <Input
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Catégorie</Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="Organisation, Productivité..."
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* SEO Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Aperçu Google</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
                  <p className="text-blue-700 font-medium truncate">
                    {formData.seo_title || formData.title || 'Titre de l\'article'}
                  </p>
                  <p className="text-green-700 text-xs truncate">
                    recrutermalin.com/blog/{formData.slug || 'url-article'}
                  </p>
                  <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                    {formData.seo_description || formData.excerpt || 'Description de l\'article...'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
